import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { updateMe, uploadAvatar, deleteAvatar, getAvatarUrl } from '../services/auth';
import { useToast } from '../App';
import { HiOutlineUser, HiOutlineLockClosed, HiOutlineMail, HiOutlineCamera, HiOutlineTrash } from 'react-icons/hi';

export default function Profile() {
  const { user, loadUser } = useAuth();
  const addToast = useToast();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  // Password change
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMe({ full_name: fullName });
      await loadUser();
      addToast('success', 'Profil mis a jour avec succes');
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      addToast('error', 'Les mots de passe ne correspondent pas');
      return;
    }
    setChangingPwd(true);
    try {
      await updateMe({ password });
      addToast('success', 'Mot de passe modifie avec succes');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPwd(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      await uploadAvatar(file);
      await loadUser();
      addToast('success', 'Photo de profil mise a jour');
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await deleteAvatar();
      await loadUser();
      addToast('success', 'Photo de profil supprimee');
    } catch (err) {
      addToast('error', err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    }
    return (user?.username || '?').slice(0, 2).toUpperCase();
  };

  if (!user) return null;

  const avatarUrl = getAvatarUrl(user.avatar);

  return (
    <div>
      <div className="page-header">
        <h2><HiOutlineUser style={{ marginRight: 8 }} /> Mon Profil</h2>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto' }}>
      {/* Avatar */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div className="profile-avatar-wrapper">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="profile-avatar-img" />
            ) : (
              <div className="profile-avatar-placeholder">{getInitials()}</div>
            )}
            <button
              className="profile-avatar-overlay"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              title="Changer la photo"
            >
              <HiOutlineCamera size={20} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1.05rem', color: 'var(--navy-800)' }}>
              {user.full_name || user.username}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginTop: 2 }}>
              {{ superadmin: 'Super administrateur', admin: 'Administrateur' }[user.role] || 'Utilisateur'}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
              <button
                className="btn btn-outline-primary btn-sm"
                style={{ fontSize: '0.75rem' }}
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? 'Upload...' : 'Changer la photo'}
              </button>
              {user.avatar && (
                <button
                  className="btn btn-outline-danger btn-sm"
                  style={{ fontSize: '0.75rem' }}
                  onClick={handleDeleteAvatar}
                  disabled={uploadingAvatar}
                >
                  <HiOutlineTrash style={{ marginRight: 2 }} /> Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <h6 style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>Informations generales</h6>
          <form onSubmit={handleUpdateProfile}>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-username">Nom d'utilisateur</label>
              <input id="field-username" className="form-control form-control-sm" value={user.username} disabled />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-email"><HiOutlineMail style={{ marginRight: 4 }} /> Email</label>
              <input id="field-email" className="form-control form-control-sm" value={user.email} disabled />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-full-name">Nom complet</label>
              <input
                id="field-full-name"
                className="form-control form-control-sm"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Votre nom complet"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-role">Role</label>
              <input id="field-role" className="form-control form-control-sm" value={{ superadmin: 'Super administrateur', admin: 'Administrateur' }[user.role] || 'Utilisateur'} disabled />
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </form>
        </div>
      </div>

      {/* Changement mot de passe */}
      <div className="card">
        <div className="card-body">
          <h6 style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
            <HiOutlineLockClosed style={{ marginRight: 4 }} /> Changer le mot de passe
          </h6>
          <form onSubmit={handleChangePassword}>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-new-password">Nouveau mot de passe</label>
              <input
                id="field-new-password"
                type="password"
                className="form-control form-control-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 caracteres, 1 majuscule, 1 chiffre"
                minLength={8}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor="field-confirm-password">Confirmer le mot de passe</label>
              <input
                id="field-confirm-password"
                type="password"
                className="form-control form-control-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repetez le mot de passe"
                minLength={8}
                required
              />
            </div>
            <button type="submit" className="btn btn-outline-warning btn-sm" disabled={changingPwd}>
              {changingPwd ? 'Modification...' : 'Modifier le mot de passe'}
            </button>
          </form>
        </div>
      </div>
      </div>{/* end max-width wrapper */}
    </div>
  );
}
